import React, { useEffect, useState } from "react";
import { useI18n } from "../i18n";
import { getAssetClasses, postSimulator } from "../lib/api";
import { MultiLineChart, COLORS } from "../components/Charts";
import LocationSelector from "../components/LocationSelector";
import CountUp from "react-countup";

export default function Simulator() {
  const { t, lang } = useI18n();
  const [assets, setAssets] = useState([]);
  const [form, setForm] = useState({ asset_id: "real_estate", amount_usd: 200000, years: 10, city: "Shanghai", units: 5 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getAssetClasses().then((d) => setAssets(d.asset_classes || []));
  }, []);

  const run = async () => {
    setLoading(true);
    try {
      const d = await postSimulator({ ...form, lang });
      setResult(d);
    } finally {
      setLoading(false);
    }
  };

  const chartSeries = result
    ? [
        { label: t.common.scenario_bear, data: result.series.bear.map((p) => ({ year: p.year, value: p.value })), color: COLORS.red },
        { label: t.common.scenario_base, data: result.series.base.map((p) => ({ year: p.year, value: p.value })), color: COLORS.emerald },
        { label: t.common.scenario_bull, data: result.series.bull.map((p) => ({ year: p.year, value: p.value })), color: COLORS.gold },
      ]
    : [];

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-16" data-testid="page-simulator">
      <div>
        <span className="text-xs uppercase tracking-[0.2em] text-sky-400 font-mono">
          {lang === "pt" ? "Projeção de cenários" : "Scenario projection"}
        </span>
        <h1 className="mt-2 font-display text-4xl lg:text-5xl tracking-tight">{t.simulator.title}</h1>
        <p className="mt-3 max-w-2xl text-neutral-400">{t.simulator.sub}</p>
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 surface p-5 space-y-4" data-testid="simulator-controls">
          <Field label={lang === "pt" ? "Classe de ativo" : "Asset class"}>
            <select
              value={form.asset_id}
              onChange={(e) => setForm({ ...form, asset_id: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold-500"
              data-testid="sim-asset-select"
            >
              {assets.map((a) => (
                <option key={a.asset_id} value={a.asset_id}>
                  {lang === "pt" ? a.name_pt : a.name_en}
                </option>
              ))}
            </select>
          </Field>

          {form.asset_id === "real_estate" && (
            <div data-testid="sim-location">
              <label className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-mono block mb-2">
                {lang === "pt" ? "Localização do imóvel" : "Property location"}
              </label>
              <LocationSelector
                value={form.city}
                onChange={(city) => setForm({ ...form, city })}
                testidPrefix="sim-loc"
                compact
              />
            </div>
          )}

          <Field label={t.common.amount}>
            <input
              type="number"
              value={form.amount_usd}
              onChange={(e) => setForm({ ...form, amount_usd: +e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-right font-mono text-sm focus:outline-none focus:border-gold-500"
              data-testid="sim-amount-input"
            />
          </Field>

          {form.asset_id === "real_estate" && (
            <Field label={t.common.units}>
              <input
                type="number"
                value={form.units}
                onChange={(e) => setForm({ ...form, units: Math.max(1, +e.target.value || 1) })}
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-right font-mono text-sm focus:outline-none focus:border-gold-500"
                data-testid="sim-units-input"
              />
            </Field>
          )}

          <Field label={`${lang === "pt" ? "Horizonte" : "Horizon"} (${t.common.years})`}>
            <input
              type="range"
              min={1}
              max={25}
              value={form.years}
              onChange={(e) => setForm({ ...form, years: +e.target.value })}
              className="w-full accent-gold-500"
              data-testid="sim-years-range"
            />
            <div className="text-right font-mono text-sm text-gold-400 mt-1">
              {form.years} {t.common.years}
            </div>
          </Field>

          <button
            onClick={run}
            disabled={loading}
            className="w-full bg-gold-500 hover:bg-gold-400 disabled:opacity-40 text-black font-medium py-3 rounded-md transition-colors"
            data-testid="run-simulator"
          >
            {loading ? "…" : t.common.simulate}
          </button>
        </div>

        <div className="lg:col-span-8 space-y-6">
          {result && (
            <div className="grid sm:grid-cols-3 gap-4" data-testid="simulator-cards">
              {[
                { key: "bear", label: t.common.scenario_bear, color: COLORS.red, testid: "card-bear" },
                { key: "base", label: t.common.scenario_base, color: COLORS.emerald, testid: "card-base" },
                { key: "bull", label: t.common.scenario_bull, color: COLORS.gold, testid: "card-bull" },
              ].map((s) => (
                <div key={s.key} className="surface p-4" data-testid={s.testid}>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                    <span className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-mono">
                      {s.label}
                    </span>
                  </div>
                  <div className="mt-2 font-mono text-2xl text-white">
                    $<CountUp end={result.final[s.key]} duration={1.2} separator="," decimals={0} />
                  </div>
                  <div className="text-xs text-neutral-500 mt-1 font-mono">
                    {lang === "pt" ? "ao fim de" : "after"} {result.years} {t.common.years}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="surface p-5">
            <h3 className="font-display text-lg mb-3">
              {lang === "pt" ? "Cenários" : "Scenarios"}
            </h3>
            {result ? (
              <MultiLineChart series={chartSeries} years={result.years} testid="simulator-chart" />
            ) : (
              <div className="h-[320px] grid place-items-center text-sm text-neutral-500">
                {lang === "pt" ? "Execute o simulador para visualizar." : "Run the simulator to visualize."}
              </div>
            )}
          </div>

          {result && (
            <div className="surface p-5" data-testid="simulator-takeaway">
              <div className="text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
                {t.takeaway}
              </div>
              <p className="mt-2 text-sm text-neutral-300 leading-relaxed">{result.takeaway}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-mono block mb-1.5">
      {label}
    </label>
    {children}
  </div>
);
